import { useState } from "react"
import generateWatchProjectImage from "../../utils/generateWatchProjectImage"

const GalleryMain = ({ salesWatch, setActiveImage, activeImage, setIsThreesixtyImagesViewOpen }: GalleryMainProps) => {
  const { watch } = salesWatch

  const [touchStart, setTouchStart] = useState<(number | null)>(null)
  const [touchEnd, setTouchEnd] = useState<(number | null)>(null)

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX)

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      if (activeImage === 0) return setActiveImage(salesWatch.gallery.length)
      setActiveImage(activeImage - 1)
    }

    if (isRightSwipe) {
      if (activeImage === salesWatch.gallery.length) return setActiveImage(0)
      setActiveImage(activeImage + 1)
    }
  }

  return (
    <div className="lg:w-2/5 lg:mr-2">
      <div className="bg-white h-full flex items-center relative">
        {salesWatch.threesixtyImages && salesWatch.threesixtyImages.length && (
          <button
            onClick={() => setIsThreesixtyImagesViewOpen(true)}
            className="bg-primary font-cirka font-bold text-white text-lg absolute bottom-5 right-5 py-2 px-5 rounded-full"
          >
            360° view
          </button>
        )}
        <img
          src={generateWatchProjectImage(salesWatch.imagePath, watch.referenceNumber)}
          alt={`${watch.brand ? watch.brand.name : ''} ${watch.model ? watch.model.name : ''}`}
          className={`mx-auto object-scale-down aspect-square md:h-550 lg:h-600 ${activeImage === 0 ? '' : 'hidden'}`}
          loading="eager"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        />
        {salesWatch.gallery && salesWatch.gallery.map((imageUrl, index) => (
          <img
            src={generateWatchProjectImage(imageUrl, watch.referenceNumber)}
            alt={`${watch.brand ? watch.brand.name : ''} ${watch.model ? watch.model.name : ''}`}
            className={`mx-auto object-scale-down aspect-square md:h-550 lg:h-600 ${activeImage === index + 1 ? '' : 'hidden'}`}
            key={`salesWatch-gallery-${index}`}
            loading="lazy"
            width={600}
            height={600}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />
        ))}
      </div>
      <div className="flex lg:hidden justify-center my-5 lg:my-0">
        <div
          className={`w-10 h-2 ${activeImage === 0 ? 'bg-primary' : 'border border-primary hover:bg-primary hover:border-none'} mx-2 rounded-full cursor-pointer`}
          onClick={() => setActiveImage(0)}
        />
        {salesWatch.gallery && salesWatch.gallery.map((imageUrl, index) => (
          <div
            className={`w-10 h-2 ${activeImage === index + 1 ? 'bg-primary' : 'border border-primary hover:bg-primary hover:border-none'} mx-2 rounded-full cursor-pointer`}
            onClick={() => setActiveImage(index + 1)}
            key={`salesWatch-gallery-bubble-${index}`}
          />
        ))}
      </div>
    </div>
  )
}

const GalleryPreviews = ({ salesWatch, setActiveImage }: GalleryPreviewProps) => {
  const { watch } = salesWatch

  return (
    <>
      <img
        src={generateWatchProjectImage(salesWatch.imagePath, watch.referenceNumber)}
        alt={`${watch.brand ? watch.brand.name : ''} ${watch.model ? watch.model.name : ''}`}
        className="w-24 h-16 object-scale-down cursor-pointer border-2 border-primary mr-1 hover:shadow-md"
        onClick={() => setActiveImage(0)}
        loading="lazy"
      />
      {salesWatch.gallery && salesWatch.gallery.map((imageUrl, index) => (
        <img
          src={generateWatchProjectImage(imageUrl, watch.referenceNumber)}
          alt={`${watch.brand ? watch.brand.name : ''} ${watch.model ? watch.model.name : ''}`}
          className="w-24 h-16 object-scale-down cursor-pointer border-2 border-primary mr-1 hover:shadow-md"
          onClick={() => setActiveImage(index + 1)}
          loading="lazy"
          key={`salesWatch-gallery-previews-${index}`}
        />
      ))}
    </>
  )
}



interface GalleryPreviewProps {
  salesWatch: WatchProject21Api.SalesWatch
  setActiveImage: (index: number) => void
}

interface GalleryMainProps extends GalleryPreviewProps {
  activeImage: number
  setIsThreesixtyImagesViewOpen: (isOpen: boolean) => void
}

export { GalleryMain, GalleryPreviews }
